import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, MessageCircle, Play, Sparkles, Heart, Brain, Flame, Shield, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const courseData = {
  tratak: {
    name: "Diya Tratak Sadhana",
    subtitle: "Steady Gaze Meditation with Ghee Lamp",
    price: 1500,
    duration: "Monthly",
    benefits: [
      "Calms the restless mind and reduces mental chatter",
      "Strengthens focus and inner concentration",
      "Awakens the Ajna (Third Eye) chakra naturally"
    ],
    freeTrialPath: "/maya-manthan/wisdom",
    freeTrialLabel: "Try a 5-minute guided Tratak practice",
    icon: Flame,
    color: "sacred-gold",
    warning: "Practice only with Diya (ghee lamp or mustard oil). Do NOT use candles as they are considered Tamasik."
  },
  chakra: {
    name: "Chakra Balancing Program",
    subtitle: "3-Month Deep Healing Journey",
    price: 3000,
    duration: "Monthly for 3 months",
    benefits: [
      "Identify and heal blocked energy centers",
      "Restore emotional and physical balance",
      "Experience deeper states of meditation"
    ],
    freeTrialPath: "/maya-manthan/assessment",
    freeTrialLabel: "Take the free Chakra Assessment first",
    icon: Heart,
    color: "sacred-purple"
  },
  tridevi: {
    name: "Tridevi Sadhana",
    subtitle: "4-Month Divine Feminine Practice",
    price: 4000,
    duration: "Monthly for 4 months",
    benefits: [
      "Connect with Durga, Lakshmi, and Saraswati energies",
      "Transform obstacles into opportunities",
      "Cultivate wisdom, abundance, and strength"
    ],
    freeTrialPath: "/maya-manthan/wisdom",
    freeTrialLabel: "Explore the Tridevi teachings",
    icon: Sparkles,
    color: "sacred-blue"
  },
  ganesh: {
    name: "Ganesh Sadhana",
    subtitle: "3-Month Obstacle Removal Practice",
    price: 2000,
    duration: "Monthly for 3 months",
    benefits: [
      "Remove inner and outer obstacles",
      "Develop clarity in decision-making",
      "Invoke auspicious beginnings in all endeavors"
    ],
    freeTrialPath: "/maya-manthan/wisdom",
    freeTrialLabel: "Learn about Ganesh mantras",
    icon: Shield,
    color: "sacred-green"
  }
};

const UPI_ID = "indrajitch@icici";
const WHATSAPP_NUMBER = "919876543210"; // Replace with actual number

export default function CourseEnrollmentModal({ isOpen, onClose, courseId, onNavigate }) {
  const [step, setStep] = useState(1);
  const [selectedOption, setSelectedOption] = useState(null);
  
  const course = courseData[courseId];
  
  if (!isOpen || !course) return null;
  
  const handleClose = () => {
    setStep(1);
    setSelectedOption(null);
    onClose();
  };
  
  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    if (option === 'upi') {
      setStep(3);
    } else if (option === 'whatsapp') {
      const message = encodeURIComponent(`Hi, I want to start my practice with ${course.name}. Please guide me.`);
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
    } else if (option === 'free') {
      handleClose();
      if (onNavigate) {
        onNavigate(course.freeTrialPath);
      }
    }
  };
  
  const handlePaymentConfirm = () => {
    setStep(4);
    toast.success("Thank you! Your enrollment is being processed.");
  };
  
  const handleWhatsAppJoin = () => {
    const message = encodeURIComponent(`Hi, I want to start my practice. I have enrolled in ${course.name}. Please add me to the guidance group.`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };
  
  const IconComponent = course.icon;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-full max-w-lg bg-[#12182B]/95 backdrop-blur-2xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="enrollment-modal-close"
          >
            <X className="w-5 h-5 text-sacred-text-secondary" />
          </button>
          
          {/* Progress Indicator */}
          <div className="absolute top-4 left-4 flex gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  s === step ? 'w-6 bg-sacred-gold' : s < step ? 'bg-sacred-gold/50' : 'bg-white/20'
                }`}
              />
            ))}
          </div>
          
          {/* Step 1: Program Introduction */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-8 pt-14"
            >
              {/* Icon & Title */}
              <div className="text-center mb-8">
                <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-${course.color}/20 to-${course.color}/5 flex items-center justify-center`}
                  style={{ 
                    background: course.color === 'sacred-gold' ? 'linear-gradient(135deg, rgba(212, 175, 55, 0.2), rgba(212, 175, 55, 0.05))' :
                               course.color === 'sacred-purple' ? 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(168, 85, 247, 0.05))' :
                               course.color === 'sacred-blue' ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.05))' :
                               'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.05))'
                  }}
                >
                  <IconComponent className="w-10 h-10" style={{ 
                    color: course.color === 'sacred-gold' ? '#D4AF37' :
                           course.color === 'sacred-purple' ? '#A855F7' :
                           course.color === 'sacred-blue' ? '#3B82F6' : '#22C55E'
                  }} />
                </div>
                <h2 className="font-heading text-2xl text-sacred-text-primary mb-2">
                  {course.name}
                </h2>
                <p className="text-sacred-text-secondary text-sm font-body">
                  {course.subtitle}
                </p>
              </div>
              
              {/* Emotional Positioning */}
              <div className="bg-gradient-to-r from-sacred-gold/10 to-transparent border-l-2 border-sacred-gold p-4 rounded-r-xl mb-6">
                <p className="text-sacred-text-primary font-heading text-lg italic">
                  "You are about to begin a deeper inner journey"
                </p>
              </div>
              
              {/* Benefits */}
              <div className="space-y-3 mb-8">
                <p className="text-sacred-text-muted text-sm font-body uppercase tracking-wide">What Awaits You</p>
                {course.benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-sacred-green/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-sacred-green" />
                    </div>
                    <p className="text-sacred-text-secondary font-body text-sm">{benefit}</p>
                  </div>
                ))}
              </div>
              
              {/* Warning for Tratak */}
              {course.warning && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6">
                  <p className="text-amber-400 text-sm font-body">
                    <strong>Sacred Practice Note:</strong> {course.warning}
                  </p>
                </div>
              )}
              
              {/* Price */}
              <div className="text-center mb-6">
                <p className="text-sacred-gold font-heading text-3xl">₹{course.price.toLocaleString()}</p>
                <p className="text-sacred-text-muted text-sm font-body">{course.duration}</p>
              </div>
              
              {/* CTA */}
              <Button
                onClick={() => setStep(2)}
                className="w-full bg-gradient-to-r from-sacred-gold to-sacred-gold-light text-sacred-text-on-gold font-body font-medium py-4 rounded-full hover:shadow-gold-glow transition-all duration-300"
                data-testid="enrollment-continue-btn"
              >
                Begin My Practice
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          )}
          
          {/* Step 2: Choose Your Path */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-8 pt-14"
            >
              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl text-sacred-text-primary mb-2">
                  Choose Your Path
                </h2>
                <p className="text-sacred-text-secondary text-sm font-body">
                  How would you like to begin?
                </p>
              </div>
              
              <div className="space-y-4">
                {/* Option 1: Start Instantly */}
                <button
                  onClick={() => handleOptionSelect('upi')}
                  className="w-full p-5 rounded-xl border border-sacred-gold/30 bg-gradient-to-r from-sacred-gold/10 to-transparent hover:from-sacred-gold/20 transition-all duration-300 text-left group"
                  data-testid="enrollment-upi-option"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-sacred-gold/20 flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-sacred-gold" />
                      </div>
                      <div>
                        <p className="font-heading text-lg text-sacred-text-primary">Start Instantly</p>
                        <p className="text-sacred-text-muted text-sm font-body">Pay via UPI • ₹{course.price.toLocaleString()}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-sacred-gold group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
                
                {/* Option 2: Talk to Guide */}
                <button
                  onClick={() => handleOptionSelect('whatsapp')}
                  className="w-full p-5 rounded-xl border border-sacred-green/30 bg-gradient-to-r from-sacred-green/10 to-transparent hover:from-sacred-green/20 transition-all duration-300 text-left group"
                  data-testid="enrollment-whatsapp-option"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-sacred-green/20 flex items-center justify-center">
                        <MessageCircle className="w-6 h-6 text-sacred-green" />
                      </div>
                      <div>
                        <p className="font-heading text-lg text-sacred-text-primary">Talk to Your Guide</p>
                        <p className="text-sacred-text-muted text-sm font-body">Get personal guidance on WhatsApp</p>
                      </div>
                    </div>
                    <ExternalLink className="w-5 h-5 text-sacred-green group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
                
                {/* Option 3: Free Trial */}
                <button
                  onClick={() => handleOptionSelect('free')}
                  className="w-full p-5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-300 text-left group"
                  data-testid="enrollment-free-option"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-sacred-purple/20 flex items-center justify-center">
                        <Play className="w-6 h-6 text-sacred-purple" />
                      </div>
                      <div>
                        <p className="font-heading text-lg text-sacred-text-primary">Try a Free Practice First</p>
                        <p className="text-sacred-text-muted text-sm font-body">{course.freeTrialLabel}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-sacred-purple group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              </div>
              
              <button
                onClick={() => setStep(1)}
                className="w-full mt-6 text-sacred-text-muted text-sm font-body hover:text-sacred-text-secondary transition-colors"
              >
                ← Back to program details
              </button>
            </motion.div>
          )}
          
          {/* Step 3: Payment Screen */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-8 pt-14"
            >
              <div className="text-center mb-8">
                <h2 className="font-heading text-2xl text-sacred-text-primary mb-2">
                  Complete Your Offering
                </h2>
                <p className="text-sacred-text-secondary text-sm font-body">
                  Scan QR or use UPI ID below
                </p>
              </div>
              
              {/* QR Code Placeholder */}
              <div className="bg-white rounded-2xl p-4 mx-auto w-48 h-48 mb-6 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center mb-2">
                    {/* QR Code would go here - using placeholder */}
                    <svg viewBox="0 0 100 100" className="w-28 h-28">
                      <rect x="10" y="10" width="20" height="20" fill="#000"/>
                      <rect x="40" y="10" width="10" height="10" fill="#000"/>
                      <rect x="60" y="10" width="10" height="10" fill="#000"/>
                      <rect x="70" y="10" width="20" height="20" fill="#000"/>
                      <rect x="10" y="40" width="10" height="10" fill="#000"/>
                      <rect x="30" y="40" width="10" height="10" fill="#000"/>
                      <rect x="50" y="40" width="10" height="10" fill="#000"/>
                      <rect x="80" y="40" width="10" height="10" fill="#000"/>
                      <rect x="10" y="70" width="20" height="20" fill="#000"/>
                      <rect x="40" y="60" width="10" height="10" fill="#000"/>
                      <rect x="60" y="70" width="10" height="10" fill="#000"/>
                      <rect x="80" y="80" width="10" height="10" fill="#000"/>
                    </svg>
                  </div>
                  <p className="text-xs text-gray-600 font-body">Scan to pay</p>
                </div>
              </div>
              
              {/* UPI ID */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
                <p className="text-sacred-text-muted text-xs font-body uppercase tracking-wide mb-2">UPI ID</p>
                <div className="flex items-center justify-between">
                  <p className="text-sacred-text-primary font-mono text-lg">{UPI_ID}</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(UPI_ID);
                      toast.success("UPI ID copied!");
                    }}
                    className="text-sacred-gold text-sm font-body hover:underline"
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              {/* Amount */}
              <div className="text-center mb-8">
                <p className="text-sacred-text-muted text-sm font-body">Amount to Pay</p>
                <p className="text-sacred-gold font-heading text-4xl">₹{course.price.toLocaleString()}</p>
              </div>
              
              {/* Confirmation Button */}
              <Button
                onClick={handlePaymentConfirm}
                className="w-full bg-gradient-to-r from-sacred-gold to-sacred-gold-light text-sacred-text-on-gold font-body font-medium py-4 rounded-full hover:shadow-gold-glow transition-all duration-300"
                data-testid="enrollment-paid-btn"
              >
                <Check className="w-5 h-5 mr-2" />
                I Have Made the Payment
              </Button>
              
              <button
                onClick={() => setStep(2)}
                className="w-full mt-4 text-sacred-text-muted text-sm font-body hover:text-sacred-text-secondary transition-colors"
              >
                ← Choose different option
              </button>
            </motion.div>
          )}
          
          {/* Step 4: Confirmation */}
          {step === 4 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="p-8 pt-14 text-center"
            >
              {/* Success Icon */}
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-sacred-green/20 to-sacred-green/5 flex items-center justify-center animate-sacred-breathing">
                <Check className="w-10 h-10 text-sacred-green" />
              </div>
              
              <h2 className="font-heading text-2xl text-sacred-text-primary mb-2">
                Your Journey Begins
              </h2>
              <p className="text-sacred-text-secondary text-sm font-body mb-8">
                Thank you for taking this sacred step. Your enrollment for <strong>{course.name}</strong> is being processed.
              </p>
              
              {/* WhatsApp Join Card */}
              <div className="bg-gradient-to-br from-sacred-green/10 to-sacred-green/5 border border-sacred-green/30 rounded-xl p-6 mb-6">
                <MessageCircle className="w-8 h-8 text-sacred-green mx-auto mb-4" />
                <h3 className="font-heading text-lg text-sacred-text-primary mb-2">
                  Join WhatsApp for Guidance
                </h3>
                <p className="text-sacred-text-secondary text-sm font-body mb-4">
                  Connect with your guide for practice schedules, daily reminders, and personalized support.
                </p>
                <Button
                  onClick={handleWhatsAppJoin}
                  className="bg-sacred-green hover:bg-sacred-green/90 text-white font-body font-medium px-6 py-3 rounded-full"
                  data-testid="enrollment-whatsapp-join"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Join Guidance Group
                </Button>
              </div>
              
              {/* Next Steps */}
              <div className="bg-white/5 rounded-xl p-5 mb-6 text-left">
                <p className="text-sacred-text-muted text-xs font-body uppercase tracking-wide mb-3">What Happens Next</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-sacred-text-secondary font-body">
                    <span className="text-sacred-gold">1.</span>
                    Your payment will be verified within 24 hours
                  </li>
                  <li className="flex items-start gap-2 text-sm text-sacred-text-secondary font-body">
                    <span className="text-sacred-gold">2.</span>
                    You'll receive access to the practice materials
                  </li>
                  <li className="flex items-start gap-2 text-sm text-sacred-text-secondary font-body">
                    <span className="text-sacred-gold">3.</span>
                    Your guide will reach out personally
                  </li>
                </ul>
              </div>
              
              <Button
                onClick={handleClose}
                variant="ghost"
                className="text-sacred-text-muted font-body hover:text-sacred-text-secondary"
              >
                Return to Courses
              </Button>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
